import React, { useMemo, useState } from "react";
import "../../../css/View/SummaryFinance.css";
import Element from "../../AddTripForm/Elements/Element";

const SummaryFinance = ({ variant, elements, onUpdated }) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [clickedElement, setClickedElement] = useState(null);

  const rows = useMemo(() => {
    return (elements || []).map((el) => {
      const capacity =
        el.capacity === null || el.capacity === undefined || el.capacity === ""
          ? null
          : Number(el.capacity);

      const unit =
        el.pricePerPerson === null || el.pricePerPerson === undefined || el.pricePerPerson === ""
          ? null
          : Number(el.pricePerPerson);

      const fullCapacityCost =
        capacity !== null && !Number.isNaN(capacity) && unit !== null && !Number.isNaN(unit)
          ? capacity * unit
          : null;

      const invoice =
        el.invoiceAmount === null || el.invoiceAmount === undefined || el.invoiceAmount === ""
          ? null
          : Number(el.invoiceAmount);

      const diff =
        fullCapacityCost !== null && !Number.isNaN(fullCapacityCost) && invoice !== null && !Number.isNaN(invoice)
          ? fullCapacityCost - invoice
          : null;

      return {
        ...el,
        _capacity: capacity,
        _unit: unit,
        _fullCapacityCost: fullCapacityCost,
        _invoiceAmount: invoice,
        _diff: diff,
      };
    });
  }, [elements]);

  const groupedByDate = useMemo(() => {
    const grouped = {};
    rows.forEach((el) => {
      const date = el.startDate ? el.startDate.split("T")[0] : "Brak daty";
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(el);
    });

    return Object.entries(grouped)
      .sort(([a], [b]) => (a > b ? 1 : -1))
      .map(([date, els]) => [
        date,
        els.sort((x, y) => new Date(x.startDate || 0) - new Date(y.startDate || 0)),
      ]);
  }, [rows]);

  const elementsPricePerPerson = useMemo(() => {
    return (elements || []).reduce((sum, el) => {
      const raw = el?.pricePerPerson;
      if (raw === null || raw === undefined || raw === "") return sum;
      const n = Number(raw);
      if (Number.isNaN(n)) return sum;
      return sum + n;
    }, 0);
  }, [elements]);

  const fmtPln = (n) => {
    if (n === null || n === undefined || Number.isNaN(n)) return "-";
    return `${Number(n).toFixed(2)}\u00A0zł`;
  };

  const fmtCap = (n) => {
    if (n === null || n === undefined || Number.isNaN(n)) return "-";
    return n;
  };

  const openEdit = (el) => {
    setClickedElement(el);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setClickedElement(null);
  };

  const handleSaved = async () => {
    closeEdit();
    await onUpdated?.();
  };

  const getDiffClass = (diff) => {
    if (diff === null || diff === undefined || Number.isNaN(diff)) return "";
    if (diff > 0) return "diff-positive";
    if (diff < 0) return "diff-negative";
    return "diff-zero";
  };

  const showTourist = !!variant?.isTouristEvent;
  const showAbroad = !!variant?.isTripAbroad;

  return (
    <div className="summary-finance-container">
      {/* ===== Informacje ogólne ===== */}
      <h3>Informacje ogólne</h3>

      {(showTourist || showAbroad) && (
        <div className="summary-finance-tags">
          {showTourist && <span className="summary-finance-tag">Impreza turystyczna</span>}
          {showAbroad && <span className="summary-finance-tag">Wyjazd zagraniczny</span>}
        </div>
      )}

      <div className="summary-finance-priceBlock">
        <div className="summary-finance-priceMain">
          <span className="label">Koszt na osobę:</span>
          <span className="value strong">{fmtPln(variant?.pricePerPerson)}</span>
        </div>

        <div className="summary-finance-breakdown">
          <div className="break-row">
            <span className="break-label">Kwota z elementów</span>
            <span className="break-value">{fmtPln(elementsPricePerPerson)}</span>
          </div>

          <div className="break-row">
            <span className="break-label">Koszty administracyjne</span>
            <span className="break-value">{fmtPln(variant?.administrativeCosts)}</span>
          </div>

          {showTourist && (
            <div className="break-row">
              <span className="break-label">TFG</span>
              <span className="break-value">{fmtPln(variant?.tfgContribution)}</span>
            </div>
          )}

          {showTourist && showAbroad && (
            <div className="break-row">
              <span className="break-label">NNW</span>
              <span className="break-value">{fmtPln(variant?.nnwContribution)}</span>
            </div>
          )}
        </div>
      </div>
      <h3>Koszty elementów</h3>
      <div className="summary-finance-hint">
        💡 Kliknij w wiersz elementu, aby go edytować.
      </div>

      <div className="summary-finance-scroll">
        <table className="summary-finance-table">
          <thead>
            <tr>
              <th>Element</th>
              <th>Pojemność</th>
              <th className="nowrap">Cena / os.</th>
              <th>Koszt</th>
              <th>Faktura</th>
              <th>Różnica</th>
              <th>Komentarz</th>
            </tr>
          </thead>

          <tbody>
            {groupedByDate.map(([date, els]) => (
              <React.Fragment key={date}>
                <tr className="day-header">
                  <td colSpan="7">📅 {date}</td>
                </tr>

                {els.map((el) => (
                  <tr
                    key={el.guid}
                    className="summary-clickable-row"
                    onClick={() => openEdit(el)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") openEdit(el);
                    }}
                    title="Kliknij aby edytować element"
                  >
                    <td>{el.name}</td>
                    <td>{fmtCap(el._capacity)}</td>
                    <td className="money-cell">{fmtPln(el._unit)}</td>
                    <td className="money-cell">{fmtPln(el._fullCapacityCost)}</td>
                    <td className="money-cell">{fmtPln(el._invoiceAmount)}</td>
                    <td className={`money-cell ${getDiffClass(el._diff)}`}>{fmtPln(el._diff)}</td>
                    <td className="summary-comment-cell">{el.internalComment || "-"}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {isEditOpen && clickedElement && (
        <div className="popup">
          <div className="popupContent nicePopup summary-edit-modal">
            <Element
              initialData={clickedElement}
              variantGuid={variant?.guid}
              onSave={handleSaved}
              onDelete={handleSaved}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryFinance;
